import { useLazyQuery } from '@apollo/client';

import { EventbriteEvent } from '../../components/event-details';
import { useAuth } from '../../contexts/auth';
import { apolloClient } from '../apollo';
import QueryCurrentUser from './current-user.graphql';
import MutationUpdateMailchimpInterestSubscription from './update-mailchimp-interest-subscription.graphql';

interface ProjectStatistic {
	label?: string;
	value?: string;
	type?: 'percentage' | 'currency';
}

interface CurrentUser {
	id: string;
	email: string;
	groups: Array<string>;
	projects: Array<{
		id: string;
		title?: string;
		mailchimpInterestId?: string;
		statistics?: Array<ProjectStatistic>;
	}>;
	eventOrders: Array<{
		id: string;
		attendees: Array<{
			id: string;
			name: string;
			barcodes: Array<string>;
		}>;
		event: EventbriteEvent;
	}>;
	mailchimpMember?: {
		id: string;
		email: string;
		subscribedInterests: Array<{
			id: string;
			name: string;
			interestCategory: {
				id: string;
				title: string;
			};
		}>;
	};
	tenderSubmissions: Array<{
		tenderId: string;
		tender: {
			title: string;
			stage: string;
			status: string;
			slug: string;
			summary: string;
			closingDate: Date;
			procedureType: string;
			contractType: string;
			contractValue: number;
			contactName: string;
			contactEmail: string;
		};
		id: string;
		status: string;
		name: string;
		email: string;
		company: string;
		links: string;
		comments: string;
		uploads: [
			{
				id: string;
				url: string;
			}
		];
	}>;
}

export const useCurrentUser = () => {
	const { session } = useAuth();

	const [executeQuery, { data, loading, error, refetch }] = useLazyQuery<{
		currentUser: CurrentUser;
	}>(QueryCurrentUser, {
		client: apolloClient,
		context: { accessToken: session?.accessToken },
		notifyOnNetworkStatusChange: true,
		errorPolicy: 'all',
	});

	const subscribedMailchimpInterests =
		data?.currentUser?.mailchimpMember?.subscribedInterests ?? [];

	const isMailchimpMember: boolean | undefined =
		(!loading && !!data?.currentUser?.mailchimpMember) ?? undefined;

	const updateMailchimpInterestSubscription = async (interestId: string, subscribed: boolean) => {
		const { data } = await apolloClient.mutate({
			mutation: MutationUpdateMailchimpInterestSubscription,
			variables: { interestId, subscribed },
			context: { accessToken: session?.accessToken },
		});

		if (!data.updateMailchimpInterestSubscription) throw new Error('Failed to update subscription');
	};

	if (session && !loading && !data) {
		executeQuery();
	}

	return {
		loading,
		error,
		user: data?.currentUser,
		subscribedMailchimpInterests,
		isMailchimpMember,
		updateMailchimpInterestSubscription,
		refetch,
	};
};
